/** Fetches the documentation model once per session; both the dialog and /developers read it. */
import { useEffect, useState } from "react";
import type { DocsModel } from "@hydra-notes/shared";

let cached: DocsModel | null = null;
let inflight: Promise<DocsModel | null> | null = null;

export function loadDocs(): Promise<DocsModel | null> {
  if (cached) return Promise.resolve(cached);
  inflight ??= fetch("/api/v1/docs.json")
    .then((r) => (r.ok ? (r.json() as Promise<DocsModel>) : null))
    .then((m) => {
      cached = m;
      inflight = null;
      return m;
    })
    .catch(() => {
      inflight = null;
      return null;
    });
  return inflight;
}

export function useDocs(): { docs: DocsModel | null; failed: boolean } {
  const [docs, setDocs] = useState<DocsModel | null>(cached);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (docs) return;
    let alive = true;
    void loadDocs().then((m) => {
      if (!alive) return;
      if (m) setDocs(m);
      else setFailed(true);
    });
    return () => {
      alive = false;
    };
  }, [docs]);
  return { docs, failed };
}

/** The base the docs describe; falls back to this origin before they load. */
export function docsBase(docs: DocsModel | null): string {
  return docs?.baseUrl ?? location.origin;
}

export function mcpUrl(docs: DocsModel | null): string {
  return docs?.mcpUrl ?? `${location.origin}/mcp`;
}

/** Copy-paste snippets, one per way of connecting. */
export function snippets(docs: DocsModel | null, key = "hn_your_key_here") {
  const mcp = mcpUrl(docs);
  const base = docsBase(docs);
  return {
    claudeOauth: `claude mcp add --scope user --transport http hydra-notes ${mcp}`,
    claudeKey: `claude mcp add --scope user --transport http hydra-notes ${mcp} \\\n  --header "Authorization: Bearer ${key}"`,
    jsonOauth: JSON.stringify({ mcpServers: { "hydra-notes": { type: "http", url: mcp } } }, null, 2),
    jsonKey: JSON.stringify({ mcpServers: { "hydra-notes": { type: "http", url: mcp, headers: { Authorization: `Bearer ${key}` } } } }, null, 2),
    rest: `curl ${base}/api/v1/me -H "Authorization: Bearer ${key}"`,
    restTask: `curl -X POST ${base}/api/v1/tasks \\\n  -H "Authorization: Bearer ${key}" -H "Content-Type: application/json" \\\n  -d '{"title": "Water the plants"}'`,
  };
}
